import { getCustomProps, getNotCustomProps, getSubCustomProps } from '../forms'
import { getData } from '../get-table'
import { HttpService } from '../http.service'

const http = new HttpService()

export interface Employee {
  id?: string
  code?: string
  payrollId: string
  firstName: string
  secondName?: string
  lastname: string
  secondLastname?: string
  documentCountryId: string
  identificationNumber: string
  socialInsuranceNumber: string
  fiscalId?: string
  positionId: string
  occupationAreaId: string
  journeyId: string
  sucursalId: string
  directionId: string
  managementId: string
  departmentId: string
  turnId: string
  employeeTypeId: string
  contractTypeId: string
  joiningReasonId: string
  joiningDate: string
  leaveReasonId: string
  leaveDate?: string
  hasMainEmploymentRelationship?: boolean
  insuranceClassId?: string
  isPensioner?: boolean
  pensionTypeId?: string
  isRecontractable?: boolean
  isCloseToLiquidation?: boolean
  belongsToAssociation?: boolean
  isReEnteredEmployee?: boolean
  nationalityId: string
  birthCountryId?: string
  birthStateId?: string
  birthCityId?: string
  genderId?: string
  maritalStatusId: string
  educationLevelId?: string
  isForeigner?: boolean
  bloodGroup?: string
  isDisabledPerson?: boolean
  disabilityTypeId?: string
  isLeftHanded?: boolean
  residenceCountryId?: string
  residenceStateId?: string
  residenceCityId?: string
  addressLocation?: string
  zipCode?: string
  personalEmail?: string
  workEmail?: string
  primaryPhoneNumber?: string
  secundaryPhoneNumber?: string
  hasDriverLicense?: boolean
  driverLicenseCategory?: string
  remoteDays?: any[]
  birthDate: string
  supervisorName?: string
  height?: string
  weight?: string
  shirtSize?: string
  flannelSize?: string
  trouserSize?: string
  briefsSize?: string
  shoesSize?: string
  observation?: string
  histories?: object
  employerRecordId?: string
  workCenterId?: string
  accounts?: Accounts[]
  salaries?: Salaries[]
  relatives?: Relatives[]
  identifications?: Identifications[]
  costCenters?: CostCenters[]
  educationLevels?: EducationLevels[]
  customFields: any
  recognitionDate?: string
  codeChange?: string
  additionalCodifications?: { [key: string]: string }
}

export interface Salaries {
  amount: string
  appliesFrom: string
  baseSalaryId: string
  currencyId: string
  increaseReasonId: string
  observations?: string
}

export interface Accounts {
  accountNumber: string
  appliesFrom: string
  bankId: string
  bankTypeId: string
  currencyId: string
  percentage: string
  salaryPaymentTypeId: string
  bankSucursalId?: string
  benefitBankId?: string
  intermediaryBankId?: string
  isActiveAccount?: boolean
  paymentBaseId?: string
  accountTypeId?: string
  walletAddress?: string
  id?: string
  employeeId?: string
  customFields: any
}

export interface Relatives {
  identificationNumber: string
  relationshipTypeId: string
  address?: string
  disabilityTypeId?: string
  educationLevelId?: string
  email?: string
  firstName?: string
  identificationTypeId?: string
  isDependent: boolean
  isEmergencyContact: boolean
  isStudent: boolean
  lastname?: string
  nationalityId?: string
  phoneNumber?: string
  secondLastname?: string
  secondName?: string
  percentage: string
}

export interface Identifications {
  identificationNumber: string
  identificationTypeId: string
}

export interface CostCenters {
  costCenterId: string
  percentage: string
}

export interface EducationLevels {
  educationLevelId: string
  name?: string
  description?: string
}

export interface EmployeeCodeChangeRequest {
  employeeId: string
  newCode: string
  oldCode: string
  observation: string
  requestStatusCode: string
  includeHistoricalUpdate: boolean
  userName: string
  errors: any[]
  errorMessage: string
  createdAt: string
  updatedAt: string
}

export interface EmployeeReentryRequest {
  employeeId: string
  entryDate: string
  recognitionDate: string
  keepSalary: boolean
  keepBankAccounts: boolean
  keepAccountingDistribution: boolean
  keepEducationLevels: boolean
  keepRelatives: boolean
  keepIdentifications: boolean
}

export async function getEmployees(
  offset: number,
  perPage: number,
  dataOrder: string,
  search: string = '',
  country: string = '',
  includeInactive: boolean = false
): Promise<any> {
  // where's
  const where = ['payroll_id = :payrollId']
  if (search) {
    where.push(
      `CONCAT(employees.first_name, employees.second_name, employees.lastname, employees.second_lastname, employees.code, employees.identification_number, employees.joining_date, employees.leave_date, departments.name, sucursals.name, positions.name, journeys.name, turns.name, employees.recognition_date) ILIKE '%${search}%'`
    )
  }
  // Filter by leave_date if includeInactive is false
  if (!includeInactive) {
    where.push('employees.leave_date IS NULL')
  }
  // order by
  let modifier = []
  dataOrder !== ''
    ? dataOrder[1] === 'code'
      ? (modifier = [
          `ORDER BY (CASE WHEN employees.code ~ '^[0-9]+$' THEN CAST(employees.code AS NUMERIC) ELSE NULL END) ${dataOrder[0]}, employees.code ${dataOrder[0]}`
        ])
      : (modifier = [`order by ${dataOrder[1]} ${dataOrder[0]}`])
    : (modifier = [
        "ORDER BY (CASE WHEN employees.code ~ '^[0-9]+$' THEN CAST(employees.code AS NUMERIC) ELSE NULL END), employees.code"
      ])
  // result
  const response: any = await getData({
    tables: ['pg_employees'],
    fields: [
      'employees.id',
      'employees.code as code_employee',
      "CONCAT(employees.first_name,' ',employees.second_name,' ',employees.lastname,' ',employees.second_lastname) as name_employee",
      'employees.identification_number',
      'employees.joining_date',
      'employees.leave_date',
      'departments.name as department',
      'sucursals.name as sucursal',
      'positions.name as position',
      'journeys.name as journey',
      'turns.name as turn',
      'employees.recognition_date',
      'employees.updated_at',
      "CONCAT(employees.code,' - ',employees.first_name,' ',coalesce(employees.second_name,''),' ',employees.lastname,' ',coalesce(employees.second_lastname,'')) as codigo_name",
      ...(country == 'MX'
        ? [
            "employees.custom_fields->>'custom:mxFamilyMedicineUnit' as family_medicine_unit",
            'co1."name" as type_salary',
            'co2."name" as sua_workday',
            'co3."name" as salary_assimilation',
            "employees.custom_fields->>'custom:mxColony' as colony",
            'co4."name" as sua_contract_type',
            'co5."name" as sat_workday_type'
          ]
        : [])
    ],
    join: [
      'INNER JOIN departments ON departments.id = employees.department_id',
      'INNER JOIN sucursals ON sucursals.id = employees.sucursal_id',
      'INNER JOIN positions ON positions.id = employees.position_id',
      'INNER JOIN journeys ON journeys.id = employees.journey_id',
      'INNER JOIN turns ON turns.id = employees.turn_id',
      "LEFT JOIN custom_options co1 on co1.id = (employees.custom_fields->>'custom:mxTypeSalary')::uuid",
      "LEFT JOIN custom_options co2 on co2.id = (employees.custom_fields->>'custom:mxSuaWorkday')::uuid",
      "LEFT JOIN custom_options co3 on co3.id = (employees.custom_fields->>'custom:mxSalaryAssimilation')::uuid",
      "LEFT JOIN custom_options co4 on co4.id = (employees.custom_fields->>'custom:mxSuaContractType')::uuid",
      "LEFT JOIN custom_options co5 on co5.id = (employees.custom_fields->>'custom:mxSatWorkdayType')::uuid"
    ],
    where,
    modifier,
    options: {
      outputFormat: 'json',
      pagination: { offset, limit: perPage }
    }
  })
  return response
}

export async function getCodeChanges(
  employeeId: string,
  offset: number,
  perPage: number,
  dataOrder: string
): Promise<any> {
  let modifier = []
  if (dataOrder !== '') {
    if (dataOrder[1] === 'code') {
      modifier = [
        `ORDER BY (CASE WHEN employee_code_change_requests.new_code ~ '^[0-9]+$' THEN CAST(employee_code_change_requests.new_code AS NUMERIC) ELSE NULL END) ${dataOrder[0]}, employee_code_change_requests.new_code ${dataOrder[0]}`
      ]
    } else {
      modifier = [`order by ${dataOrder[1]} ${dataOrder[0]}`]
    }
  } else {
    modifier = ['ORDER BY employee_code_change_requests.created_at DESC']
  }
  // result
  const response: any = await getData({
    tables: ['pg_employee_code_change_requests'],
    fields: [
      'employee_code_change_requests.id',
      'employee_code_change_requests.employee_id as employeeId',
      'employee_code_change_requests.new_code as newCode',
      'employee_code_change_requests.old_code as oldCode',
      'employee_code_change_requests.observation as observation',
      'request_status.name as requestStatusName',
      `CASE WHEN employee_code_change_requests.include_historical_update = true THEN 'SI' ELSE 'NO' END as includeHistoricalUpdate`,
      'employee_code_change_requests.user_name as userName',
      'employee_code_change_requests.errors as errors',
      "'' as errorMessage",
      'employee_code_change_requests.created_at as createdAt',
      'employee_code_change_requests.updated_at as updatedAt'
    ],
    join: [
      'INNER JOIN employees ON employee_code_change_requests.employee_id = employees.id',
      'INNER JOIN request_status ON employee_code_change_requests.request_status_id = request_status.id'
    ],
    where: [`employee_id = '${employeeId}'`],
    modifier,
    options: {
      outputFormat: 'json',
      pagination: { offset, limit: perPage }
    }
  })
  return response
}

export async function checkPendingEmployeeCodeChangeRequests(
  payrollId: string
): Promise<any> {
  const data = await getData<any>({
    fields: [
      'DISTINCT ON (employee_code_change_requests.employee_id) employee_code_change_requests.employee_id',
      'rs.code as request_status_code'
    ],
    tables: ['pg_employee_code_change_requests'],
    join: [
      'INNER JOIN employees em ON employee_code_change_requests.employee_id = em.id',
      'INNER JOIN request_status rs ON employee_code_change_requests.request_status_id = rs.id'
    ],
    where: [`em.payroll_id = '${payrollId}'`],
    modifier: [
      'ORDER BY employee_code_change_requests.employee_id, employee_code_change_requests.created_at DESC'
    ]
  })
  return data
}

export async function getEmployeeCodeType(payrollId: string): Promise<any> {
  const data = await getData<any>({
    tables: ['pg_payrolls'],
    fields: ['employee_code_types.name'],
    join: [
      'INNER JOIN employee_code_types ON payrolls.employee_code_type = employee_code_types.id'
    ],
    where: [`payrolls.id = '${payrollId}'`]
  })
  return data[0]?.results[0]?.name
}

export async function getEmployeeReentries(payrollId: string): Promise<any> {
  const data = await getData<any>({
    tables: ['pg_employee_reentry_requests'],
    fields: ['employee_id'],
    join: [
      'INNER JOIN employees ON employee_reentry_requests.employee_id = employees.id'
    ],
    where: [`employees.payroll_id = '${payrollId}'`]
  })
  return data[0]?.results
}

export async function getEmployee(id: string): Promise<Employee> {
  const data = await getData<any>({
    tables: ['pg_employees'],
    fields: [
      'id',
      'payroll_id as payrollId',
      'first_name as firstName',
      'second_name as secondName',
      'lastname',
      'second_lastname as secondLastname',
      'document_country_id as documentCountryId',
      'identification_type_id as identificationTypeId',
      'identification_number as identificationNumber',
      'social_insurance_number as socialInsuranceNumber',
      "TO_CHAR(birth_date,'yyyy-mm-dd') as birthDate",
      'nationality_id as nationalityId',
      'employee_type_id as employeeTypeId',
      'contract_type_id as contractTypeId',
      'joining_reason_id as joiningReasonId',
      "TO_CHAR(joining_date,'yyyy-mm-dd') as joiningDate",
      'leave_reason_id as leaveReasonId',
      "TO_CHAR(leave_date,'yyyy-mm-dd') as leaveDate",
      'fiscal_id as fiscalId',
      'position_id as positionId',
      'occupation_area_id as occupationAreaId',
      'journey_id as journeyId',
      'sucursal_id as sucursalId',
      'direction_id as directionId',
      'management_id as managementId',
      'department_id as departmentId',
      'turn_id as turnId',
      'birth_country_id as birthCountryId',
      'birth_state_id as birthStateId',
      'birth_city_id as birthCityId',
      'gender_id as genderId',
      'marital_status_id as maritalStatusId',
      'is_pensioner as isPensioner',
      'pension_type_id as pensionTypeId',
      'insurance_class_id as insuranceClassId',
      'has_driver_license as hasDriverLicense',
      'driver_license_category as driverLicenseCategory',
      'is_disabled_person as isDisabledPerson',
      'disability_type_id as disabilityTypeId',
      'residence_country_id as residenceCountryId',
      'residence_state_id as residenceStateId',
      'residence_city_id as residenceCityId',
      'address_location as addressLocation',
      'zip_code as zipCode',
      'personal_email as personalEmail',
      'is_foreigner as isForeigner',
      'remote_days as remoteDays',
      'blood_group as bloodGroup',
      'work_email as workEmail',
      'primary_phone_number as primaryPhoneNumber',
      'secundary_phone_number as secundaryPhoneNumber',
      'has_main_employment_relationship as hasMainEmploymentRelationship',
      'is_left_handed as isLeftHanded',
      'is_recontractable as isRecontractable',
      'belongs_to_association as belongsToAssociation',
      'is_close_to_liquidation as isCloseToLiquidation',
      'is_re_entered_employee as isReEnteredEmployee',
      'supervisor_name as supervisorName',
      'height',
      'weight',
      'shirt_size as shirtSize',
      'flannel_size as flannelSize',
      'trouser_size as trouserSize',
      'briefs_size as briefsSize',
      'shoes_size as shoesSize',
      'observation',
      'code',
      'custom_fields as custom',
      'employer_record_id as employerRecordId',
      'work_center_id as workCenterId',
      "TO_CHAR(recognition_date,'yyyy-mm-dd') as recognitionDate",
      'additional_codifications as additionalCodifications',
      'contract_execution_state_id as contractExecutionStateId'
    ],
    where: [`employees.id = '${id}'`]
  })
  return data[0].results[0]
}

export async function createEmployee(data: Employee): Promise<boolean> {
  delete data.histories
  const response: any = await http.post('employee', {
    ...getNotCustomProps(data),
    accounts: getSubCustomProps(data.accounts),
    customFields: {
      ...getCustomProps(data)
    }
  })

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function updateEmployee(
  data: Employee,
  id: string
): Promise<boolean> {
  for (const account of data.accounts ?? []) {
    account.percentage = account.percentage.toString()
    account.percentage = account.percentage.includes('%')
      ? account.percentage.slice(0, -1)
      : account.percentage
  }
  for (const costCenter of data.costCenters ?? []) {
    costCenter.percentage = costCenter.percentage.toString()
    costCenter.percentage = costCenter.percentage.includes('%')
      ? costCenter.percentage.slice(0, -1)
      : costCenter.percentage
  }
  for (const relative of data.relatives ?? []) {
    relative.percentage = relative.percentage.toString()
    relative.percentage = relative.percentage.includes('%')
      ? relative.percentage.slice(0, -1)
      : relative.percentage
  }
  delete data.code
  delete data.histories
  const response: any = await http.put('employee/' + id, {
    ...getNotCustomProps(data),
    accounts: getSubCustomProps(data.accounts),
    customFields: {
      ...getCustomProps(data)
    }
  })

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function deleteEmployee(id: string): Promise<boolean> {
  const response: any = await http.delete('employee/' + id)

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function createEmployeeCodeChangeRequest(
  employeeId: string,
  newCode: string,
  observation: string,
  includeHistoricalUpdate: boolean
): Promise<boolean> {
  const response: any = await http.post('employee/code-change-requests', {
    employeeId,
    newCode,
    observation,
    includeHistoricalUpdate
  })

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function createEmployeeReentryRequest(
  data: EmployeeReentryRequest
): Promise<boolean> {
  const response: any = await http.post('employee/reentry-requests', {
    employeeId: data.employeeId,
    entryDate: data.entryDate,
    recognitionDate: data.recognitionDate,
    keepSalary: data.keepSalary,
    keepBankAccounts: data.keepBankAccounts,
    keepAccountingDistribution: data.keepAccountingDistribution,
    keepEducationLevels: data.keepEducationLevels,
    keepRelatives: data.keepRelatives,
    keepIdentifications: data.keepIdentifications
  })

  if (response?.data?.data?.error) {
    return false
  }
  return true
}
